import controller from "@ctrl/controller";
import type { Block, BlockListType, Document } from "@ctrl/controller-rpc";

import { createBlockParagraph } from "@/components/document";
import type { BaseLogger } from "@/components/inject";

const { makeBlockId } = controller;

/**
 * Composer/editor inline content is already stored in our markdown-like "rich text"
 * format (marks are represented as markdown).
 *
 * This helper provides an explicit conversion point so call sites can depend on
 * `@/components/editor/markdown` rather than ad-hoc string passing, and we can
 * evolve the conversion later if needed.
 */
export const richTextToMarkdown = (richText: string): string =>
  typeof richText === "string" ? richText : "";

export const markupKeys = [
  "bold",
  "italic",
  "underline",
  "strikethrough",
  "code",
  "link",
] as const;
export type MarkupKey = (typeof markupKeys)[number];

export const markups = {
  bold: "**",
  italic: "*",
  underline: "_",
  strikethrough: "~~",
  code: "`",
  link: ["[", "]"],
} satisfies Record<MarkupKey, string | string[]>;
export type ContentParts = {
  beforeSelection: string;
  selection: string;
  afterSelection: string;
};
export type MarkupUsed = {
  [k in keyof typeof markups]: boolean;
};
export const initMarkupUsed = (): MarkupUsed => ({
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  code: false,
  link: false,
});
export const detectMarkups = (contentParts: ContentParts): MarkupUsed => {
  const markupUsed: MarkupUsed = initMarkupUsed();
  const isValidLinkFormat = (input: string): boolean => {
    // Regular expression for matching [anything](anything)
    const linkRegex = /^\[.*\]\(.+\)$/;
    return linkRegex.test(input);
  };

  const checkAround = (v: string) =>
    beforeSelection.endsWith(v) && afterSelection.startsWith(v);
  const checkInclusion = (v: string) =>
    selection.startsWith(v) && selection.endsWith(v);
  const { beforeSelection, selection, afterSelection } = contentParts;

  markupKeys.forEach((key) => {
    const symbols = markups[key];
    const found = () => (markupUsed[key] = true);

    // Handle specific cases
    if (Array.isArray(symbols)) {
      if (key === "link" && isValidLinkFormat(selection)) found();
    } else if (selection.replaceAll(symbols, "").length === 0) return;
    else {
      const isAroundMatched = checkAround(symbols);
      const isSelectionMatched = checkInclusion(symbols);

      // Handle specific cases
      if (key === "italic") {
        const isAroundMatchedBold = checkAround("**");
        const isSelectionMatchedBold = checkInclusion("**");
        const isAroundMatchedBoldItalic = checkAround("***");
        const isSelectionMatchedBoldItalic = checkInclusion("***");
        if (isSelectionMatchedBoldItalic) return found();
        if (isAroundMatchedBoldItalic) return found();
        if (isSelectionMatchedBold && !isAroundMatched) return;
        if (!isSelectionMatched && isAroundMatchedBold) return;
        if (
          isSelectionMatched &&
          isAroundMatched &&
          !isSelectionMatchedBold &&
          !isAroundMatchedBold
        )
          return;
      }
      if (isSelectionMatched || isAroundMatched) found();
    }
  });

  return markupUsed;
};

const indentForDepth = (depth?: number) =>
  depth && depth > 0 ? "  ".repeat(depth) : "";

const hasContent = (block: Block): block is Block & { content: string } =>
  "content" in block && typeof block.content === "string";

const normalizeContent = (block: Block): string =>
  hasContent(block) ? block.content.trim() : "";

const blockToMarkdown = (block: Block): string => {
  switch (block.type) {
    case "heading": {
      const level = Math.min(Math.max(block.level ?? 1, 1), 6);
      const content = normalizeContent(block);
      return content ? `${"#".repeat(level)} ${content}` : "";
    }
    case "paragraph": {
      return normalizeContent(block);
    }
    case "blockquote": {
      const content = normalizeContent(block);
      if (!content) return "";
      return content
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
    }
    case "bullet": {
      const content = normalizeContent(block);
      if (!content) return "";
      const indent = indentForDepth(block.depth);
      const marker = block.list_type === "ordered" ? "1." : "-";
      return `${indent}${marker} ${content}`;
    }
    case "todo": {
      const content = normalizeContent(block);
      if (!content) return "";
      const checked = block.checked ? "x" : " ";
      return `- [${checked}] ${content}`;
    }
    case "check": {
      const content = normalizeContent(block);
      if (!content) return "";
      const indent = indentForDepth(block.depth);
      const checked = block.checked ? "x" : " ";
      return `${indent}- [${checked}] ${content}`;
    }
    case "code": {
      const language = block.language?.trim() ?? "";
      const content = hasContent(block) ? block.content : "";
      return ["```" + language, content, "```"].join("\n");
    }
    case "callout": {
      const content = normalizeContent(block);
      const emoji = block.icon?.emoji;
      const prefix = emoji ? `${emoji} ` : "";
      if (!content && !emoji) return "";
      return `> ${prefix}${content}`;
    }
    case "embed": {
      return normalizeContent(block);
    }
    case "query": {
      const content = normalizeContent(block);
      return content ? `Query: ${content}` : "";
    }
    case "recurrent_task": {
      const content = normalizeContent(block);
      const recurrence = block.recurrence
        ? ` (recurs ${JSON.stringify(block.recurrence)})`
        : "";
      return content ? `- [ ] ${content}${recurrence}` : "";
    }
    case "object": {
      if ("referee" in block) {
        return `Reference (${block.referee.kind})`;
      }
      const parts = [
        block.workspace_id ? `workspace:${block.workspace_id}` : null,
        block.table_id ? `table:${block.table_id}` : null,
        block.row_id ? `row:${block.row_id}` : null,
      ].filter(Boolean);
      return parts.length > 0 ? `Object reference (${parts.join(", ")})` : "";
    }
    case "divider": {
      return "---";
    }
    default: {
      return normalizeContent(block);
    }
  }
};

const isListLikeBlock = (block: Block): boolean =>
  block.type === "bullet" ||
  block.type === "check" ||
  block.type === "todo" ||
  block.type === "recurrent_task";

export const documentToMarkdown = (
  document: Document | null | undefined
): string => {
  if (
    !document ||
    !Array.isArray(document.blocks) ||
    document.blocks.length === 0
  ) {
    return "";
  }

  return document.blocks
    .reduce<{
      markdown: string[];
      previousBlock: Block | null;
    }>(
      (acc, block) => {
        const section = blockToMarkdown(block).trim();
        if (section.length === 0) return acc;
        if (acc.markdown.length === 0) {
          acc.markdown.push(section);
        } else {
          const separator =
            acc.previousBlock &&
            isListLikeBlock(acc.previousBlock) &&
            isListLikeBlock(block)
              ? "\n"
              : "\n\n";
          acc.markdown.push(separator, section);
        }
        acc.previousBlock = block;
        return acc;
      },
      { markdown: [], previousBlock: null }
    )
    .markdown.join("");
};

// TODO: use a proper Markdown parser if we need something more robust going forward
export const markdownToDocument = (
  markdown: string,
  _logger: BaseLogger
): Document => {
  const blocks: Document["blocks"] = [];
  if (typeof markdown !== "string") return { blocks };

  const normalized = markdown.replace(/\r\n/g, "\n").trim();
  if (!normalized) return { blocks };

  const lines = normalized.split("\n");
  let index = 0;

  const peekLine = (offset = 0) => lines[index + offset] ?? "";

  const consumeLine = () => lines[index++] ?? "";

  const skipBlankLines = () => {
    while (index < lines.length && !lines[index].trim()) {
      index += 1;
    }
  };

  const createHeadingBlock = (level: number, content: string): Block => ({
    id: makeBlockId(),
    type: "heading",
    level,
    content,
  });

  const createBlockquoteBlock = (content: string): Block => ({
    id: makeBlockId(),
    type: "blockquote",
    content,
  });

  const createDividerBlock = (): Block => ({
    id: makeBlockId(),
    type: "divider",
  });

  const createBulletBlock = (
    content: string,
    listType: BlockListType,
    depth: number
  ): Block => ({
    id: makeBlockId(),
    type: "bullet",
    list_type: listType,
    depth,
    content,
  });

  const createCheckBlock = (
    content: string,
    checked: boolean,
    depth: number
  ): Block => ({
    id: makeBlockId(),
    type: "check",
    content,
    checked,
    depth,
  });

  const createCodeBlock = (content: string, language: string): Block => ({
    id: makeBlockId(),
    type: "code",
    content,
    language,
  });

  const parseCodeFence = (firstLine: string) => {
    const language = firstLine.slice(3).trim();
    const contentLines: string[] = [];
    while (index < lines.length) {
      const current = consumeLine();
      if (current.startsWith("```")) {
        break;
      }
      contentLines.push(current);
    }
    blocks.push(createCodeBlock(contentLines.join("\n"), language));
  };

  const parseBlockquote = () => {
    const quoteLines: string[] = [];
    while (index < lines.length) {
      const current = peekLine();
      if (!current.trim() || !current.trimStart().startsWith(">")) break;
      const consumed = consumeLine();
      quoteLines.push(consumed.replace(/^\s*>\s?/, ""));
    }
    blocks.push(createBlockquoteBlock(quoteLines.join("\n")));
  };

  /*
   * Bullet and checklist parsing use character-by-character logic instead of
   * regex. Regex patterns with optional/alternating parts (e.g. \s*, [-*+])
   * can cause catastrophic backtracking (ReDoS) when they don't match: the
   * engine tries exponentially many paths and can freeze the main thread on
   * long or malformed paste input. Manual parsing is O(n) with no backtracking.
   */

  /**
   * Parse checklist item [-*+] [ ] or [x] using character-by-character logic
   * instead of regex.
   *
   * Why no regex: Patterns like /^\s*[-*+]\s+\[[ xX]\]\s+/ can cause catastrophic
   * backtracking (ReDoS) on adversarial or malformed input. When the regex engine
   * tries many backtracking paths on long strings with mixed spaces and list-like
   * characters, it can block the main thread for seconds. Character-by-character
   * parsing runs in O(n) with no backtracking, so it always finishes quickly.
   */
  const parseCheckItem = (
    line: string
  ): { indent: string; marker: "x" | " "; content: string } | null => {
    let i = 0;
    while (i < line.length && (line[i] === " " || line[i] === "\t")) i++;
    const indent = line.slice(0, i);
    if (i >= line.length) return null;
    if (line[i] !== "-" && line[i] !== "*" && line[i] !== "+") return null;
    i++;
    while (i < line.length && (line[i] === " " || line[i] === "\t")) i++;
    if (i + 3 > line.length || line[i] !== "[" || line[i + 2] !== "]")
      return null;
    const m = line[i + 1];
    if (m !== " " && m !== "x" && m !== "X") return null;
    i += 3;
    while (i < line.length && (line[i] === " " || line[i] === "\t")) i++;
    return {
      indent,
      marker: m === "x" || m === "X" ? "x" : " ",
      content: line.slice(i),
    };
  };

  /**
   * Parse unordered bullet (- / * / + item) using character-by-character logic.
   *
   * Same rationale as parseCheckItem: regex patterns like `^\s*[-*+]\s+` can
   * trigger catastrophic backtracking on certain inputs. Manual parsing keeps
   * time complexity linear and prevents the app from freezing on paste.
   */
  const parseUnorderedBullet = (
    line: string
  ): { indent: string; content: string } | null => {
    let i = 0;
    while (i < line.length && /^\s$/.test(line[i] ?? "")) i++;
    const indent = line.slice(0, i);
    if (i >= line.length) return null;
    const c = line[i];
    if (c !== "-" && c !== "*" && c !== "+") return null;
    i++;
    if (i >= line.length || (line[i] !== " " && line[i] !== "\t")) return null;
    while (i < line.length && (line[i] === " " || line[i] === "\t")) i++;
    return { indent, content: line.slice(i) };
  };

  /**
   * Parse ordered list item (e.g. "1. item" or "2) item") using character-by-character
   * logic for consistency with parseUnorderedBullet/parseCheckItem and to avoid regex.
   */
  const parseOrderedBullet = (
    line: string
  ): { indent: string; content: string } | null => {
    let i = 0;
    while (i < line.length && (line[i] === " " || line[i] === "\t")) i++;
    const indent = line.slice(0, i);
    if (i >= line.length) return null;
    const digitStart = i;
    while (i < line.length && line[i] >= "0" && line[i] <= "9") i++;
    if (
      i === digitStart ||
      i >= line.length ||
      (line[i] !== "." && line[i] !== ")")
    )
      return null;
    i++;
    if (i >= line.length || (line[i] !== " " && line[i] !== "\t")) return null;
    while (i < line.length && (line[i] === " " || line[i] === "\t")) i++;
    return { indent, content: line.slice(i) };
  };

  const parseParagraph = () => {
    const paragraphLines: string[] = [];
    while (index < lines.length) {
      const current = peekLine();
      if (!current.trim()) break;
      if (/^```/.test(current)) break;
      if (/^#{1,6}\s+/.test(current.trimStart())) break;
      if (/^>\s?/.test(current.trimStart())) break;
      if (/^\s*[-*+]\s+\[[ xX]\]\s+/.test(current)) break;
      if (/^\s*([-*+])\s+/.test(current)) break;
      if (/^\s*\d+[.)]\s+/.test(current)) break;
      paragraphLines.push(consumeLine());
    }
    const content = paragraphLines.join("\n").trim();
    if (content) {
      blocks.push(createBlockParagraph(content));
    }
  };

  while (index < lines.length) {
    skipBlankLines();
    if (index >= lines.length) break;

    const line = peekLine();
    const trimmed = line.trim();

    if (!trimmed) {
      consumeLine();
      continue;
    }

    if (trimmed.startsWith("```")) {
      consumeLine();
      parseCodeFence(trimmed);
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      consumeLine();
      const [, hashes, content] = headingMatch;
      blocks.push(createHeadingBlock(hashes.length, content.trim()));
      continue;
    }

    if (/^[-*]{3,}$/.test(trimmed)) {
      consumeLine();
      blocks.push(createDividerBlock());
      continue;
    }

    if (trimmed.startsWith(">")) {
      // parseBlockquote assumes current line still available
      parseBlockquote();
      continue;
    }

    // Use parseCheckItem (not regex) to avoid ReDoS on malformed paste input
    const checkItem = parseCheckItem(line);
    if (checkItem) {
      consumeLine();
      const { indent, marker, content } = checkItem;
      const depth = Math.floor(indent.length / 2);
      blocks.push(createCheckBlock(content.trim(), marker === "x", depth));
      continue;
    }

    // Use parseUnorderedBullet (not regex) to avoid ReDoS on malformed paste input
    const unorderedBullet = parseUnorderedBullet(line);
    if (unorderedBullet) {
      consumeLine();
      const { indent, content } = unorderedBullet;
      const depth = Math.floor(indent.length / 2);
      blocks.push(createBulletBlock(content.trim(), "unordered", depth));
      continue;
    }

    const orderedBullet = parseOrderedBullet(line);
    if (orderedBullet) {
      consumeLine();
      const { indent, content } = orderedBullet;
      const depth = Math.floor(indent.length / 2);
      blocks.push(createBulletBlock(content.trim(), "ordered", depth));
      continue;
    }

    // Fallback to paragraph: parseParagraph will consume current line
    parseParagraph();
  }

  return { blocks };
};
